import { useState, useMemo, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "./index.css";
import { motion, AnimatePresence } from "framer-motion";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import { Layout, ConfigProvider, theme, Spin } from "antd";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load components for production performance
const Home = lazy(() => import("./components/Home"));
const JobStatus = lazy(() => import("./components/JobStatus"));
const Analytics = lazy(() => import("./components/Analytics"));
const Apply = lazy(() => import("./components/Apply"));
const Login = lazy(() => import("./components/Login"));
const Legal = lazy(() => import("./components/Legal"));

// Loading fallback component
const PageLoading = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    width: '100%'
  }}>
    <Spin size="large" tip="Loading..." />
  </div>
);

function AppContent() {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Read theme preference from localStorage
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  // Derive active tab from location
  const activeTab = useMemo(() => {
    const { pathname } = location;
    if (pathname.startsWith("/jobs")) return "jobs";
    if (pathname.startsWith("/analytics")) return "analytics";
    if (pathname.startsWith("/apply")) return "apply";
    if (pathname.startsWith("/home")) return "home";
    return "";
  }, [location]);

  // Optimize theme config with useMemo
  const themeConfig = useMemo(
    () => ({
      algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        colorPrimary: "#2563eb",
        borderRadius: 12,
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      components: {
        Card: {
          boxShadow: isDarkMode
            ? "0 4px 20px rgba(0,0,0,0.4)"
            : "0 4px 20px rgba(0,0,0,0.06)",
        },
        Button: {
          borderRadius: 8,
          controlHeight: 40,
        },
      },
    }),
    [isDarkMode],
  );

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <ErrorBoundary>
        <AnimatePresence mode="wait">
          <Suspense fallback={<PageLoading />}>
            <Routes location={location} key={location.pathname}>
              {/* Public route */}
              <Route path="/login" element={<Login />} />

              {/* Main application routes */}
              <Route
                path="/*"
                element={
                  <Layout
                    style={{
                      minHeight: "100vh",
                      background: isDarkMode ? "#0a0a0a" : "#f8fafc",
                      transition: "background-color 0.3s ease, color 0.3s ease",
                    }}
                    className={isDarkMode ? "dark-mode" : "light-mode"}
                  >
                    <Navigation
                      activeTab={activeTab}
                      isDarkMode={isDarkMode}
                      onToggleTheme={toggleTheme}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{ width: "100%" }}
                    >
                      <Layout.Content
                        style={{
                          padding:
                            activeTab === "home" || activeTab === "apply" ? 0 : 32,
                          maxWidth:
                            activeTab === "home" || activeTab === "apply"
                              ? "none"
                              : 1200,
                          margin: "0 auto",
                          width: "100%",
                        }}
                      >
                        <Routes>
                          <Route path="/" element={<Navigate to="/home" replace />} />
                          <Route
                            path="/home"
                            element={<Home isDarkMode={isDarkMode} />}
                          />
                          <Route
                            path="/jobs"
                            element={<JobStatus isDarkMode={isDarkMode} />}
                          />
                          <Route
                            path="/analytics"
                            element={<Analytics isDarkMode={isDarkMode} />}
                          />
                          <Route
                            path="/apply"
                            element={<Apply isDarkMode={isDarkMode} />}
                          />
                          <Route
                            path="/terms"
                            element={<Legal isDarkMode={isDarkMode} type="terms" />}
                          />
                          <Route
                            path="/privacy"
                            element={<Legal isDarkMode={isDarkMode} type="privacy" />}
                          />
                        </Routes>
                      </Layout.Content>
                      <Footer isDarkMode={isDarkMode} />
                    </motion.div>
                  </Layout>
                }
              />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </ErrorBoundary>
    </ConfigProvider>
  );
}


function App() {
  const googleClientId =
    process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID";

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
